/**
 * Tetrish - Game Board Component
 * Handles the game board creation, rendering, and piece collision detection
 */

import { BOARD_COLS, BOARD_ROWS, LINE_CLEAR_ANIM_MS } from '../constants.js';

export class GameBoard {
	constructor(gameState) {
		this.state = gameState;
		this.boardElement = document.getElementById('game-board');
		this.COLS = BOARD_COLS;
		this.ROWS = BOARD_ROWS;
		this.grid = [];
		this.cells = [];
	}

	/**
	 * Creates the game board in the DOM
	 */
	createBoard() {
		// Initialize empty grid
		this.grid = Array.from({ length: this.ROWS }, () =>
			Array.from({ length: this.COLS }, () => null));

		// Clear existing cells
		this.boardElement.innerHTML = '';
		this.cells = [];

		// Create cells
		for (let y = 0; y < this.ROWS; y++) {
			this.cells[y] = [];
			for (let x = 0; x < this.COLS; x++) {
				const cell = document.createElement('div');
				cell.classList.add('game-cell');
				cell.setAttribute('data-x', x);
				cell.setAttribute('data-y', y);
				this.boardElement.appendChild(cell);
				this.cells[y][x] = cell;
			}
		}
	}

	/**
	 * Clear the game board
	 */
	clearBoard() {
		for (let y = 0; y < this.ROWS; y++) {
			for (let x = 0; x < this.COLS; x++) {
				this.grid[y][x] = null;
				this.cells[y][x].className = 'game-cell';
			}
		}
	}

	/**
	 * Render the current game state
	 */
	render(gameState) {
		// Clear active pieces from the board (not locked pieces)
		this.clearActivePieces();

		// Render ghost piece if enabled
		if (gameState.showGhostPiece && gameState.ghostPiecePosition) {
			this.renderGhostPiece(gameState.currentPiece, gameState.ghostPiecePosition);
		}

		// Render current piece
		this.renderPiece(gameState.currentPiece);
	}

	/**
	 * Clear active (non-locked) pieces from the board
	 */
	clearActivePieces() {
		for (let y = 0; y < this.ROWS; y++) {
			for (let x = 0; x < this.COLS; x++) {
				// Only remove classes from cells that aren't locked in place
				if (!this.grid[y][x]) {
					const cell = this.cells[y][x];
					if (cell.classList.contains('tetromino') || cell.classList.contains('tetromino-ghost')) {
						cell.className = 'game-cell';
					}
				}
			}
		}
	}

	/**
	 * Render a piece on the board
	 */
	renderPiece(piece) {
		if (!piece) return;

		const shape = piece.getCurrentShape();
		const position = piece.position;
		const type = piece.type;

		shape.forEach((row, y) => {
			row.forEach((value, x) => {
				if (value) {
					const boardX = position.x + x;
					const boardY = position.y + y;

					if (this.isWithinBounds(boardX, boardY)) {
						const cell = this.cells[boardY][boardX];
						cell.classList.add('tetromino', `tetromino-${type}`);
					}
				}
			});
		});
	}

	/**
	 * Render ghost piece (preview of where piece will land)
	 */
	renderGhostPiece(piece, ghostPosition) {
		if (!piece) return;

		const shape = piece.getCurrentShape();
		const type = piece.type;

		shape.forEach((row, y) => {
			row.forEach((value, x) => {
				if (value) {
					const boardX = ghostPosition.x + x;
					const boardY = ghostPosition.y + y;

					if (this.isWithinBounds(boardX, boardY)) {
						const cell = this.cells[boardY][boardX];
						cell.classList.add('tetromino-ghost', `tetromino-${type}`);
					}
				}
			});
		});
	}

	/**
	 * Calculate ghost piece position (where the piece will land)
	 */
	updateGhostPiece(gameState) {
		if (!gameState.currentPiece) return;

		const currentPiece = gameState.currentPiece;
		const ghostPiece = currentPiece.clone();

		// Drop the ghost piece to the bottom
		while (!this.checkCollision(ghostPiece)) {
			ghostPiece.move(0, 1);
		}

		// Move back up one step (last valid position)
		ghostPiece.move(0, -1);

		// Store ghost position
		gameState.ghostPiecePosition = {
			x: ghostPiece.position.x,
			y: ghostPiece.position.y
		};
	}

	/**
	 * Check if a piece collides with the board boundaries or locked pieces
	 */
	checkCollision(piece) {
		const shape = piece.getCurrentShape();
		const position = piece.position;

		for (let y = 0; y < shape.length; y++) {
			for (let x = 0; x < shape[y].length; x++) {
				if (shape[y][x]) {
					const boardX = position.x + x;
					const boardY = position.y + y;

					// Check if out of bounds
					if (!this.isWithinBounds(boardX, boardY)) {
						return true;
					}

					// Check if cell is already occupied by a locked piece
					if (this.grid[boardY][boardX]) {
						return true;
					}
				}
			}
		}

		return false;
	}

	/**
	 * Check if coordinates are within board boundaries
	 */
	isWithinBounds(x, y) {
		return x >= 0 && x < this.COLS && y >= 0 && y < this.ROWS;
	}

	/**
	 * Detect a T-spin using the standard 3-corner rule.
	 * Must be called BEFORE locking the piece.
	 * Returns: 'tspin' | 'tspin-mini' | null
	 */
	checkTspin(piece) {
		if (piece.type !== 't') return null;

		const { x, y } = piece.position;

		// The 4 corner positions of the T-piece's 3×3 bounding box
		const corners = [[x, y], [x + 2, y], [x, y + 2], [x + 2, y + 2]];

		// A corner is "occupied" if it is out-of-bounds or contains a locked cell
		const occupied = corners.map(([cx, cy]) => {
			if (cx < 0 || cx >= this.COLS || cy < 0 || cy >= this.ROWS) return true;
			return this.grid[cy][cx] !== null;
		});

		const count = occupied.filter(Boolean).length;
		if (count >= 3) return 'tspin';
		if (count < 2) return null;

		// Exactly 2 corners — mini T-spin only if BOTH front corners are occupied
		// Front corners (the direction the T's stem points toward) by rotation state:
		const frontByRotation = { 0: [0, 1], 1: [1, 3], 2: [2, 3], 3: [0, 2] };
		const front = frontByRotation[piece.rotation];
		return front.every(i => occupied[i]) ? 'tspin-mini' : null;
	}

	/**
	 * Returns true if the board will be empty after all pending line clears.
	 * (Works correctly even before the asynchronous DOM removal runs, because
	 * a complete row will be cleared and an already-empty row stays empty.)
	 */
	isBoardClear() {
		return this.grid.every(row =>
			row.every(cell => cell === null) || row.every(cell => cell !== null)
		);
	}

	/**
	 * Lock a piece in place on the grid
	 */
	lockPiece(piece) {
		if (!piece) return 0;

		const shape = piece.getCurrentShape();
		const position = piece.position;
		const type = piece.type;

		// Lock piece on the grid
		shape.forEach((row, y) => {
			row.forEach((value, x) => {
				if (value) {
					const boardX = position.x + x;
					const boardY = position.y + y;

					if (this.isWithinBounds(boardX, boardY)) {
						this.grid[boardY][boardX] = type;
						this.cells[boardY][boardX].className = 'game-cell tetromino tetromino-' + type;
					}
				}
			});
		});

		// Check for completed lines
		return this.clearLines();
	}

	/**
	 * Check and clear completed lines
	 */
	clearLines() {
		const completeRows = [];

		// Check each row from bottom to top
		for (let y = this.ROWS - 1; y >= 0; y--) {
			if (this.grid[y].every(cell => cell !== null)) {
				// Add animation class to the row that will be cleared
				for (let x = 0; x < this.COLS; x++) {
					this.cells[y][x].classList.add('animate-clear-line');
				}
				completeRows.push(y);
			}
		}

		if (completeRows.length > 0) {
			// Use a single timeout so all rows are cleared together after the animation.
			// Sort descending (bottom rows first) so each clearLine call operates on
			// correct indices — clearing a lower row shifts upper rows down, but the
			// next row index we clear is already above the shift boundary.
			setTimeout(() => {
				completeRows.sort((a, b) => b - a);
				for (const row of completeRows) {
					this.clearLine(row);
				}
			}, LINE_CLEAR_ANIM_MS);
		}

		return completeRows.length;
	}

	/**
	 * Clear a specific line and move everything down
	 */
	clearLine(row) {
		// Move all rows above this row down by one
		for (let y = row; y > 0; y--) {
			for (let x = 0; x < this.COLS; x++) {
				this.grid[y][x] = this.grid[y - 1][x];

				// Update cell appearance
				if (this.grid[y][x]) {
					this.cells[y][x].className = 'game-cell tetromino tetromino-' + this.grid[y][x];
				} else {
					this.cells[y][x].className = 'game-cell';
				}
			}
		}

		// Clear the top row
		for (let x = 0; x < this.COLS; x++) {
			this.grid[0][x] = null;
			this.cells[0][x].className = 'game-cell';
		}
	}
}
